import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import HomeButton from '../components/HomeButton';
import { ArrowLeft, Building, Plus, Trash2, Edit, Save, X, Search, ArrowUpDown } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GestionarCiudades = () => {
  const navigate = useNavigate();
  const [ciudades, setCiudades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('nombre');
  const [sortDirection, setSortDirection] = useState('asc');
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [formData, setFormData] = useState({
    nombre: '',
    region: ''
  });

  useEffect(() => {
    fetchCiudades();
  }, []);

  const fetchCiudades = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/ciudades`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCiudades(response.data);
    } catch (err) {
      setError('Error al cargar ciudades');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/ciudades`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFormData({ nombre: '', region: '' });
      fetchCiudades();
    } catch (err) {
      setError('Error al crear ciudad');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (ciudad) => {
    setEditingId(ciudad.id);
    setEditData({ nombre: ciudad.nombre, region: ciudad.region || '' });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditData({});
  };

  const handleSaveEdit = async (ciudadId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/ciudades/${ciudadId}`, editData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditingId(null);
      setEditData({});
      fetchCiudades();
    } catch (err) {
      setError('Error al actualizar ciudad');
      console.error(err);
    }
  };

  const handleDelete = async (ciudadId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta ciudad?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/ciudades/${ciudadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchCiudades();
    } catch (err) {
      setError('Error al eliminar ciudad');
      console.error(err);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredAndSortedCiudades = ciudades
    .filter(ciudad => 
      ciudad.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (ciudad.region || '').toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      const aValue = a[sortField] || '';
      const bValue = b[sortField] || '';
      const comparison = aValue.localeCompare(bValue);
      return sortDirection === 'asc' ? comparison : -comparison;
    });

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-2 mb-4">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver
          </Button>
          <HomeButton />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Formulario */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Plus className="h-5 w-5" />
                Crear Nueva Ciudad
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="nombre">Nombre *</Label>
                  <Input
                    id="nombre"
                    value={formData.nombre}
                    onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="region">Región</Label>
                  <Input
                    id="region"
                    value={formData.region}
                    onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                  />
                </div>
                <Button type="submit" disabled={saving} className="w-full">
                  {saving ? 'Creando...' : 'Crear Ciudad'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Lista de Ciudades */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="h-5 w-5" />
                Ciudades ({filteredAndSortedCiudades.length})
              </CardTitle>
              <div className="mt-4 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Buscar por nombre o región..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </CardHeader>
            <CardContent>
              {error && (
                <Alert variant="destructive" className="mb-4">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600 mx-auto"></div>
                </div>
              ) : filteredAndSortedCiudades.length === 0 ? (
                <p className="text-center text-gray-500 py-8">
                  {searchTerm ? 'No se encontraron ciudades' : 'No hay ciudades registradas'}
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th 
                          className="text-left p-3 font-semibold cursor-pointer hover:bg-gray-50"
                          onClick={() => handleSort('nombre')}
                        >
                          <div className="flex items-center gap-2">
                            Nombre
                            <ArrowUpDown className="h-4 w-4" />
                          </div>
                        </th>
                        <th 
                          className="text-left p-3 font-semibold cursor-pointer hover:bg-gray-50"
                          onClick={() => handleSort('region')}
                        >
                          <div className="flex items-center gap-2">
                            Región
                            <ArrowUpDown className="h-4 w-4" />
                          </div>
                        </th>
                        <th className="text-right p-3 font-semibold">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAndSortedCiudades.map((ciudad) => (
                        <tr key={ciudad.id} className="border-b hover:bg-gray-50">
                          <td className="p-3">
                            {editingId === ciudad.id ? (
                              <Input
                                value={editData.nombre}
                                onChange={(e) => setEditData({ ...editData, nombre: e.target.value })}
                                className="w-full"
                              />
                            ) : (
                              <span className="font-medium">{ciudad.nombre}</span>
                            )}
                          </td>
                          <td className="p-3">
                            {editingId === ciudad.id ? (
                              <Input
                                value={editData.region}
                                onChange={(e) => setEditData({ ...editData, region: e.target.value })}
                                className="w-full"
                              />
                            ) : (
                              <span className="text-gray-600">{ciudad.region || '-'}</span>
                            )}
                          </td>
                          <td className="p-3">
                            <div className="flex justify-end gap-2">
                              {editingId === ciudad.id ? (
                                <>
                                  <Button
                                    size="sm"
                                    onClick={() => handleSaveEdit(ciudad.id)}
                                    className="bg-green-600 hover:bg-green-700"
                                  >
                                    <Save className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={handleCancelEdit}
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleEdit(ciudad)}
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    onClick={() => handleDelete(ciudad.id)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default GestionarCiudades;
